/**
 * Engine Connector Store Factory — shared engine connection management.
 *
 * Provides built-in "cloud", "local", "none" modes.
 * Apps can inject additional modes (e.g. Editor's "dev", "builtin")
 * via `modeHandlers` in the config.
 *
 * Used by both Editor and Web to create their engine connector stores.
 */

import { create, type StoreApi, type UseBoundStore } from "zustand";
import { t as translate } from "@magic-md/i18n";
import type { EngineService, EngineStatus } from "../platform";
import { useEditorStore } from "../store/editor-store";
import { usePluginExecutionStore } from "../store/plugin-execution-store";
import { createHttpEngineClient } from "./engine-http";
import { createStubEngineService } from "./engine-stub";

function tr(key: string, params?: Record<string, unknown>): string {
    return translate("editor", key, params);
}

// ── Types ──

export type BaseEngineMode = "cloud" | "local" | "none";

export interface ProbeResult {
    endpoint: string;
    alive: boolean;
    ready: boolean;
}

export interface EngineConnectorState {
    /** Current mode identifier */
    mode: string;
    /** Whether engine is connected */
    connected: boolean;
    /** Last known engine status */
    status: EngineStatus;
    /** Error message */
    error: string | null;
    /** Currently connecting */
    connecting: boolean;
    /** Connection progress message */
    connectMessage: string | null;
    /** Active EngineService */
    engineService: EngineService;

    /** Endpoint for HTTP-based modes (local/gateway) */
    localEndpoint: string;
    /** Auth token for local endpoint */
    localToken: string | null;
    /** Cloud endpoint */
    cloudEndpoint: string;

    /** Probe in progress */
    probing: boolean;
    /** Last probe result */
    probeResult: ProbeResult | null;

    // ── Actions ──
    setMode: (mode: string) => void;
    setLocalEndpoint: (endpoint: string, token?: string) => void;
    connect: () => Promise<boolean>;
    cancelConnect: () => void;
    disconnect: () => Promise<void>;
    probe: (endpoint?: string) => Promise<boolean>;
    refreshStatus: (options?: { force?: boolean }) => Promise<void>;
    getEngineService: () => EngineService;
}

// ── Mode Handler ──

export interface ModeConnectContext {
    /** Check if connection was cancelled */
    cancelled: () => boolean;
    /** Update progress message */
    setMessage: (msg: string) => void;
    /** Create an HTTP engine client */
    createHttpClient: (endpoint: string, token?: string) => EngineService;
    /** Probe an endpoint health */
    probe: (endpoint: string) => Promise<boolean>;
    /** Get current store state */
    getState: () => EngineConnectorState;
    /** Update store state */
    setState: (partial: Partial<EngineConnectorState>) => void;
}

export interface ModeConnectResult {
    service: EngineService;
    status?: EngineStatus;
}

export type ModeHandler = (
    ctx: ModeConnectContext,
) => Promise<ModeConnectResult | null>;

// ── Config ──

export interface EngineConnectorConfig {
    /** localStorage key */
    storageKey: string;
    /** Prefix for stub service name */
    stubPrefix: string;
    /** Initial mode when persisted settings do not contain an explicit mode */
    defaultMode?: string;
    /** Custom fetch function (e.g. tauriFetch for Tauri CORS bypass) */
    fetchFn?: typeof globalThis.fetch;
    /** Override initial mode from saved settings */
    getInitialMode?: (savedMode: string) => string;
    /** URL search param to read endpoint from (e.g. "engine") */
    urlEndpointParam?: string;
    /** Default local endpoint */
    defaultLocalEndpoint?: string;
    /** Default cloud endpoint */
    defaultCloudEndpoint?: string;
    /** Custom mode handlers (cloud/local/none are built-in) */
    modeHandlers?: Record<string, ModeHandler>;
    /** Called during disconnect for platform-specific cleanup (e.g. engineStop) */
    onBeforeDisconnect?: (mode: string) => Promise<void>;
    /** Reset mode to "none" on disconnect (Web behavior) */
    resetModeOnDisconnect?: boolean;
}

// ── Helpers ──

const DEFAULT_LOCAL_ENDPOINT = "http://localhost:3721";
const DEFAULT_CLOUD_ENDPOINT = "https://api-md.thevalueone.com";
const STATUS_REFRESH_TTL_MS = 3_000;

const ESSENTIAL_SIDECARS = [
    { id: "fs", command: "magic-md-fs-backend" },
] as const;

function warmupSidecars(engine: EngineService): void {
    for (const sidecar of ESSENTIAL_SIDECARS) {
        engine.sidecarSpawn(sidecar.id, sidecar.command).catch(() => { });
    }
}

// ── Factory ──

export function createEngineConnectorStore(
    config: EngineConnectorConfig,
): UseBoundStore<StoreApi<EngineConnectorState>> {
    const {
        storageKey,
        stubPrefix,
        defaultMode = "none",
        fetchFn = globalThis.fetch?.bind(globalThis),
        getInitialMode,
        urlEndpointParam,
        defaultLocalEndpoint = DEFAULT_LOCAL_ENDPOINT,
        defaultCloudEndpoint = DEFAULT_CLOUD_ENDPOINT,
        modeHandlers = {},
        onBeforeDisconnect,
        resetModeOnDisconnect = false,
    } = config;

    const stubService = createStubEngineService(stubPrefix);

    // ── Settings Persistence ──

    function loadSettings(): {
        mode: string;
        localEndpoint: string;
        cloudEndpoint: string;
        localToken: string | null;
    } {
        try {
            const raw = localStorage.getItem(storageKey);
            if (raw) {
                const data = JSON.parse(raw) as Record<string, unknown>;
                return {
                    mode: typeof data.mode === "string" ? data.mode : defaultMode,
                    localEndpoint:
                        (data.localEndpoint as string) ?? defaultLocalEndpoint,
                    cloudEndpoint: (data.cloudEndpoint as string) ?? defaultCloudEndpoint,
                    localToken: (data.localToken as string) ?? null,
                };
            }
        } catch {
            /* ignore */
        }
        return {
            mode: defaultMode,
            localEndpoint: defaultLocalEndpoint,
            cloudEndpoint: defaultCloudEndpoint,
            localToken: null,
        };
    }

    function saveSettings(state: EngineConnectorState): void {
        try {
            // Preserve extra fields (e.g. Editor's engineDir)
            const existing = JSON.parse(
                localStorage.getItem(storageKey) ?? "{}",
            ) as Record<string, unknown>;
            localStorage.setItem(
                storageKey,
                JSON.stringify({
                    ...existing,
                    mode: state.mode,
                    localEndpoint: state.localEndpoint,
                    cloudEndpoint: state.cloudEndpoint,
                    localToken: state.localToken,
                }),
            );
        } catch {
            /* ignore */
        }
    }

    // ── Probe Helper ──

    async function probeEndpoint(
        endpoint: string,
        set: (p: Partial<EngineConnectorState>) => void,
    ): Promise<boolean> {
        const normalized = endpoint.startsWith("http")
            ? endpoint
            : `http://${endpoint}`;
        set({ probing: true });

        try {
            const response = await fetchFn(normalized + "/api/health", {
                signal: AbortSignal.timeout(3_000),
            });

            if (!response.ok) {
                set({ probing: false, probeResult: null });
                return false;
            }

            const data = (await response.json()) as {
                status: string;
                engine?: { alive: boolean; ready: boolean };
            };

            const result: ProbeResult = {
                endpoint: normalized,
                alive: data.engine?.alive ?? false,
                ready: data.engine?.ready ?? false,
            };

            set({
                probing: false,
                probeResult: result,
                localEndpoint: normalized,
            });
            return result.ready;
        } catch {
            set({ probing: false, probeResult: null });
            return false;
        }
    }

    // ── Create Store ──

    return create<EngineConnectorState>((set, get) => {
        const saved = loadSettings();

        // URL parameter override for endpoint
        let initialLocalEndpoint = saved.localEndpoint;
        if (urlEndpointParam && typeof window !== "undefined") {
            const param = new URLSearchParams(window.location.search).get(
                urlEndpointParam,
            );
            if (param) {
                initialLocalEndpoint = param.startsWith("http")
                    ? param
                    : `http://${param}`;
            }
        }

        const initialMode = getInitialMode?.(saved.mode) ?? saved.mode;

        let connectAbort: AbortController | null = null;
        let statusRefreshPromise: Promise<void> | null = null;
        let lastStatusRefreshAt = 0;

        return {
            mode: initialMode,
            connected: false,
            status: { alive: false, ready: false },
            error: null,
            connecting: false,
            connectMessage: null,
            engineService: stubService,
            localEndpoint: initialLocalEndpoint,
            localToken: saved.localToken,
            cloudEndpoint: saved.cloudEndpoint,
            probing: false,
            probeResult: null,

            setMode(mode: string) {
                set({ mode });
                saveSettings({ ...get(), mode });
            },

            setLocalEndpoint(endpoint: string, token?: string) {
                const normalized = endpoint.startsWith("http")
                    ? endpoint
                    : `http://${endpoint}`;
                set({
                    localEndpoint: normalized,
                    localToken: token ?? get().localToken,
                });
                saveSettings(get());
            },

            cancelConnect() {
                if (connectAbort) {
                    connectAbort.abort();
                    connectAbort = null;
                }
                set({
                    connecting: false,
                    connectMessage: null,
                    error: tr("engineConnector.cancelled"),
                });
            },

            async connect(): Promise<boolean> {
                const state = get();

                if (state.connecting && connectAbort) {
                    connectAbort.abort();
                }

                const abort = new AbortController();
                connectAbort = abort;
                set({
                    connecting: true,
                    error: null,
                    connectMessage: tr("engineConnector.preparing"),
                });

                const cancelled = () => abort.signal.aborted;

                const ctx: ModeConnectContext = {
                    cancelled,
                    setMessage: (msg) => set({ connectMessage: msg }),
                    createHttpClient: (endpoint, token) =>
                        createHttpEngineClient({ endpoint, token, fetchFn }),
                    probe: (ep) => probeEndpoint(ep, set),
                    getState: get,
                    setState: (partial) => set(partial),
                };

                try {
                    // Save & close documents when switching away from active engine
                    if (state.connected && state.mode !== "none") {
                        await useEditorStore.getState().saveAllAndCloseTabs();
                        usePluginExecutionStore.getState().resetForEngineSwitch();
                    }

                    let result: ModeConnectResult | null = null;

                    // Check custom handlers first, then built-in
                    const custom = modeHandlers[state.mode];
                    if (custom) {
                        result = await custom(ctx);
                    } else {
                        switch (state.mode) {
                            case "cloud": {
                                if (cancelled()) return false;
                                ctx.setMessage(tr("engineConnector.connectingCloud"));
                                result = {
                                    service: ctx.createHttpClient(state.cloudEndpoint),
                                };
                                break;
                            }

                            case "local": {
                                const endpoint = state.localEndpoint;
                                if (!endpoint) {
                                    set({
                                        connecting: false,
                                        connectMessage: null,
                                        error: tr("engineConnector.endpointRequired"),
                                    });
                                    return false;
                                }
                                if (cancelled()) return false;
                                ctx.setMessage(tr("engineConnector.connectingEndpoint", { endpoint }));

                                const ready = await ctx.probe(endpoint);
                                if (cancelled()) return false;

                                if (!ready) {
                                    set({
                                        connecting: false,
                                        connectMessage: null,
                                        error: tr("engineConnector.cannotConnectEndpoint", { endpoint }),
                                    });
                                    return false;
                                }

                                result = {
                                    service: ctx.createHttpClient(
                                        endpoint,
                                        state.localToken ?? undefined,
                                    ),
                                };
                                break;
                            }

                            case "none":
                            default: {
                                set({
                                    connected: false,
                                    connecting: false,
                                    connectMessage: null,
                                    status: { alive: false, ready: false },
                                    engineService: stubService,
                                    error: null,
                                });
                                return state.mode === "none";
                            }
                        }
                    }

                    if (!result || cancelled()) return false;

                    set({
                        connected: true,
                        connecting: false,
                        connectMessage: null,
                        status: result.status ?? { alive: true, ready: true },
                        engineService: result.service,
                        error: null,
                    });
                    warmupSidecars(result.service);
                    return true;
                } catch (e) {
                    if (cancelled()) return false;
                    const message = e instanceof Error ? e.message : String(e);
                    set({
                        connected: false,
                        connecting: false,
                        connectMessage: null,
                        error: message,
                        engineService: stubService,
                    });
                    return false;
                } finally {
                    connectAbort = null;
                }
            },

            async disconnect(): Promise<void> {
                if (connectAbort) {
                    connectAbort.abort();
                    connectAbort = null;
                }

                if (get().connected) {
                    await useEditorStore.getState().saveAllAndCloseTabs();
                    usePluginExecutionStore.getState().resetForEngineSwitch();
                }

                const currentMode = get().mode;
                await onBeforeDisconnect?.(currentMode);

                set({
                    connected: false,
                    connecting: false,
                    connectMessage: null,
                    status: { alive: false, ready: false },
                    engineService: stubService,
                    error: null,
                    ...(resetModeOnDisconnect ? { mode: "none" } : {}),
                });

                if (resetModeOnDisconnect) {
                    saveSettings(get());
                }
            },

            async probe(endpoint?: string): Promise<boolean> {
                const target = endpoint ?? get().localEndpoint;
                return probeEndpoint(target, set);
            },

            async refreshStatus(options?: { force?: boolean }): Promise<void> {
                const state = get();
                if (!state.connected || state.mode === "none") return;
                if (statusRefreshPromise) {
                    return statusRefreshPromise;
                }

                const now = Date.now();
                if (
                    !options?.force &&
                    lastStatusRefreshAt > 0 &&
                    now - lastStatusRefreshAt < STATUS_REFRESH_TTL_MS
                ) {
                    return;
                }

                lastStatusRefreshAt = now;
                statusRefreshPromise = (async () => {
                    try {
                        const nextStatus = await state.engineService.getEngineStatus();
                        set({ status: nextStatus, connected: nextStatus.alive });
                        if (!nextStatus.alive) {
                            set({ error: tr("engineConnector.disconnected") });
                        }
                    } catch {
                        set({
                            status: { alive: false, ready: false },
                            connected: false,
                            error: tr("engineConnector.statusUnavailable"),
                        });
                    }
                })();

                try {
                    await statusRefreshPromise;
                } finally {
                    statusRefreshPromise = null;
                }
            },

            getEngineService() {
                return get().engineService;
            },
        };
    });
}
